import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';
import { openAuthenticatedEventStream } from '../../utils/authenticatedEventStream';
import { getTransactionList } from '../../utils/transactionDataApi';
import { EmptyState, ErrorState, LoadingSkeleton } from '../ui/States';
import { confirmAction } from '../ui/ConfirmDialog';
import TransactionForm from '../Form/TransactionForm';
import { useTransactionFilters } from '../../hooks/useTransactionFilters';
import { useTransactions } from '../../hooks/useTransactions';
import { getTransactionDisplay } from '../../utils/transactionDisplay';
import { TransactionsTable } from './TransactionsTable';
import { TransactionsFilters } from './TransactionsFilters';
import { Pagination } from '../ui/Pagination';
import { AttachmentViewer } from './AttachmentViewer';
import { ExcelPasswordModal } from './ExcelPasswordModal';

const modeConfig = {
  all: { exportEmpty: 'No transactions to export.' },
  pending: { exportEmpty: 'No pending transactions to export.' },
  createApproval: { exportEmpty: 'No transactions to export.' },
  updateApproval: { exportEmpty: 'No transactions to export.' },
};


export const TransactionDataPanel = ({
  mode,
  customers,
  suppliers,
  role,
  filtersLoading,
  filtersLoaded,
  loadFilters,
  hasLinkedFilters,
  refreshVersion,
  onRefresh,
}) => {
  const { t, i18n } = useTranslation();
  const config = modeConfig[mode];
  const {
    draftFilters,
    appliedFilters,
    setFilter,
    applyFilters,
    applySingleFilter,
    resetFilters,
    currentPage,
    setCurrentPage,
    pageSize,
    changePageSize,
  } = useTransactionFilters();
  const linkedFiltersReady = !hasLinkedFilters || filtersLoaded;
  const {
    transactions: transactionData,
    totalPages,
    totalCount,
    incomes,
    expenses,
    loading,
    error,
    params: lastParams,
  } = useTransactions({
    mode,
    filters: appliedFilters,
    currentPage,
    pageSize,
    customers,
    suppliers,
    refreshVersion,
    enabled: linkedFiltersReady,
  });
  const [, setFormLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [excelExportInfo, setExcelExportInfo] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pageSizeTransition, setPageSizeTransition] = useState(false);
  const actionInFlight = useRef(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionFormType, setTransactionFormType] = useState('Add');
  const [transactionId, setTransactionId] = useState('');
  const [transactionApproveStatus, setTransactionApproveStatus] = useState('');
  const [transaction, setTransaction] = useState({
    date: '',
    customer: '',
    supplier: '',
    transaction: '',
    amount: '',
    balance: '',
    note: '',
  });

  useEffect(() => {
    if (pageSizeTransition && !loading) setPageSizeTransition(false);
  }, [loading, pageSizeTransition]);

  const changePageSizeWithLoading = (value) => {
    setPageSizeTransition(true);
    changePageSize(value);
  };
  const [attachmentViewer, setAttachmentViewer] = useState(null);

  const customerOptions = useMemo(
    () => customers.map((item) => ({
        value: item._id,
        label: `${item.firstName} ${item.lastName}`,
        firstName: item.firstName,
        lastName: item.lastName,
      })),
    [customers],
  );

  const supplierOptions = useMemo(
    () => suppliers.map((item) => ({ value: item._id, label: item.name })),
    [suppliers],
  );

  useEffect(() => {
    const streamPath = mode === 'pending' ? '/pending/updates' : '/transaction/updates';
    return openAuthenticatedEventStream(`${process.env.REACT_APP_API_URL}${streamPath}`, ({ type }) => {
      if (type === 'DELETE') {
        onRefresh();
      }
    });
  }, [mode, onRefresh]);

  const exportToExcel = async () => {
    if (exporting) return;
    if (transactionData.length === 0) {
      toast.warning(t(config.exportEmpty));
      return;
    }

    setExporting(true);
    let protectingReport = false;
    try {
      let exportRows = [];
      if (lastParams) {
        const response = await getTransactionList(mode, {
          ...lastParams,
          include_all: true,
          pageNum: 1,
          pageSize,
        });
        if (!response || response.status >= 400) throw new Error('Unable to export transactions');
        exportRows = response.data?.totalTransactions || response.data?.transactions || [];
        if (response.data?.exportTruncated) toast.warning(t('The export was limited to 10000 records.'));
      }

      const reportRows = exportRows.map((item) => {
        const display = getTransactionDisplay(item, mode);
        return {
          date: display.date,
          customer: display.customer,
          supplier: display.supplier,
          type: display.type,
          document: display.document,
          notes: display.note,
          amount: display.amount,
          attachments: display.attachments?.length || 0,
        };
      });
      const customer = customers.find((item) => item._id === appliedFilters.customerId);
      const supplier = suppliers.find((item) => item._id === appliedFilters.supplierId);
      protectingReport = true;
      const { downloadTransactionReport } = await import('../../utils/transactionExcelReport');
      const exportInfo = await downloadTransactionReport({
        rows: reportRows,
        language: i18n.language,
        mode,
        filters: {
          Cliente: customer ? `${customer.firstName} ${customer.lastName}` : '',
          Proveedor: supplier?.name || '',
          Tipo: appliedFilters.transactionType ? t(appliedFilters.transactionType) : '',
          Fecha: appliedFilters.date?.endDate || '',
          Búsqueda: appliedFilters.keyword || '',
        },
      });
      setExcelExportInfo(exportInfo);
    } catch (exportError) {
      toast.error(t(protectingReport ? 'Unable to protect the Excel report' : 'Unable to load transactions'));
    } finally {
      setExporting(false);
    }
  };

  const runAction = async (request) => {
    if (actionInFlight.current) return null;
    actionInFlight.current = true;
    setActionLoading(true);
    try {
      return await request();
    } finally {
      actionInFlight.current = false;
      setActionLoading(false);
    }
  };

  const deleteTransaction = async (id, approveStatus) => {
    const endpoint =
      approveStatus === 3 || mode === 'updateApproval'
        ? '/transaction/delete_approve_update_transaction'
        : '/transaction/delete_transaction';
    const method = endpoint.includes('delete_approve') ? 'put' : 'delete';
    const payload = method === 'put' ? { transaction_id: id } : { transaction_id: id };
    const response = await runAction(() => axiosInstance(endpoint, method, payload));
    if (!response) return;

    if (response.status === 200) {
      toast.success(t('Transaction and associated attachments deleted successfully'));
      onRefresh();
    } else {
      toast.warning(t(response.data.message));
    }
  };

  const deletePendingTransaction = async (id) => {
    const response = await runAction(() => axiosInstance('/pending/delete_pending_transaction', 'delete', {
      transaction_id: id,
    }));
    if (!response) return;

    if (response.status === 200) {
      toast.success(t('Pending transaction deleted successfully'));
      onRefresh();
    } else {
      toast.warning(t(response.data.message));
    }
  };

  const allowPendingTransaction = async (id, allowStatus) => {
    const response = await runAction(() => axiosInstance('/pending/update_pending_transaction', 'put', {
      pending_transaction_id: id,
      allowStatus,
    }));
    if (!response) return;

    if (response.status === 200) {
      toast.success(
        allowStatus === 'allow'
          ? t('Allow pending transaction successfully')
          : t('Disallow pending transaction successfully'),
      );
      onRefresh();
    } else {
      toast.warning(t(response.data.message));
    }
  };

  const approveCreatingTransaction = async (id) => {
    const response = await runAction(() => axiosInstance('/transaction/approve_create_transaction', 'put', {
      transaction_id: id,
    }));
    if (!response) return;

    if (response.status === 200) {
      toast.success(t('Transaction updated successfully'));
      onRefresh();
    } else {
      toast.warning(t(response.data.message));
    }
  };

  const approveUpdatingTransaction = async (item) => {
    const response = await runAction(() => axiosInstance('/transaction/approve_update_transaction', 'put', {
      transaction_id: item._id,
      customer_id: item.updated_customer._id,
      supplier_id: item.updated_supplier._id,
      transaction_type: item.updated_transaction_type,
      amount: item.updated_amount,
      notes: item.updated_notes,
      transaction_date: item.updated_transaction_date,
      document: item.updated_document,
      isRemove: item.isRemoved[0],
      updated_attachments: item.updated_attachments,
    }));
    if (!response) return;

    if (response.status === 200) {
      toast.success(t('Transaction updated successfully'));
      onRefresh();
    } else {
      toast.warning(t(response.data.message));
    }
  };

  const confirmDelete = (item, pending = false) => {
    confirmAction({
      title: t('Delete transaction?'),
      content: t('This action cannot be undone.'),
      okText: t('Delete'),
      cancelText: t('Cancel'),
      danger: true,
      onOk: () =>
        pending
          ? deletePendingTransaction(item.pending_transaction_id || item._id)
          : deleteTransaction(item._id, item.approve_status),
    });
  };

  const openEditForm = (item) => {
    const display = getTransactionDisplay(item, mode);
    setTransactionFormType('Edit');
    setTransaction({
      date: display.date,
      customer: display.customerId || null,
      supplier: display.supplierId || null,
      transaction: display.type,
      document: display.document,
      amount: display.amount,
      balance: item.balance,
      note: display.note,
      attachments: display.attachments || [],
    });
    setTransactionId(item._id);
    setTransactionApproveStatus(item.approve_status === 2 ? 2 : 1);
    setShowTransactionForm(true);
  };

  const ensureFilters = () => loadFilters().catch(() => {});

  const openAddForm = async () => {
    await ensureFilters();
    setTransactionFormType('Add');
    setShowTransactionForm(true);
  };

  return (
    <section className={`relative pt-4 ${actionLoading ? 'pointer-events-none' : ''}`} aria-busy={actionLoading}>
      {actionLoading && (
        <div className='absolute right-5 top-5 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-raised)] shadow-md' role='status' aria-label={t('Loading')}>
          <span className='h-4 w-4 animate-spin rounded-full border-2 border-[var(--brand)] border-t-transparent' />
        </div>
      )}
      <TransactionsFilters
        t={t}
        language={i18n.language}
        mode={mode}
        role={role}
        draftFilters={draftFilters}
        appliedFilters={appliedFilters}
        customerOptions={customerOptions}
        supplierOptions={supplierOptions}
        filtersLoading={filtersLoading}
        exporting={exporting}
        onLoadFilters={ensureFilters}
        onFilterChange={setFilter}
        onImmediateFilterChange={applySingleFilter}
        onApply={applyFilters}
        onReset={resetFilters}
        onAdd={openAddForm}
        onExport={exportToExcel}
      />

      {error && <div className='mt-5'><ErrorState title={t(error)} description={t('Try refreshing the page.')} actionLabel={t('Try again')} onAction={onRefresh} /></div>}

      <div className='app-scrollbar mt-5 overflow-x-auto rounded-[var(--radius-md)] border border-[var(--border-subtle)]'>
        {loading || pageSizeTransition ? (
          <LoadingSkeleton rows={7} />
        ) : error ? null : transactionData.length === 0 ? (
          <EmptyState title={t('No transactions found')} description={t('Adjust filters or try another section.')} />
        ) : (
          <TransactionsTable
            transactions={transactionData}
            mode={mode}
            role={role}
            pageSize={pageSize}
            currentPage={currentPage}
            incomes={incomes}
            expenses={expenses}
            t={t}
            language={i18n.language}
            onAttachment={(attachments, index) => setAttachmentViewer({ attachments, index })}
            onAllowPending={allowPendingTransaction}
            onApproveCreate={approveCreatingTransaction}
            onApproveUpdate={approveUpdatingTransaction}
            onDelete={confirmDelete}
            onEdit={openEditForm}
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={changePageSizeWithLoading}
        loading={loading || pageSizeTransition}
        t={t}
      />

      <TransactionForm
        customers={customers}
        suppliers={suppliers}
        showTransactionForm={showTransactionForm}
        setShowTransactionForm={setShowTransactionForm}
        transaction={transaction}
        type={transactionFormType}
        onClose={() => setShowTransactionForm(false)}
        setIsChanged={onRefresh}
        isChanged={Boolean(refreshVersion % 2)}
        transactionId={transactionId}
        setLoading={setFormLoading}
        transactionApproveStatus={transactionApproveStatus}
        userRole={role}
      />

      <AttachmentViewer
        open={Boolean(attachmentViewer)}
        attachments={attachmentViewer?.attachments || []}
        initialIndex={attachmentViewer?.index || 0}
        onClose={() => setAttachmentViewer(null)}
        t={t}
      />

      <ExcelPasswordModal
        exportInfo={excelExportInfo}
        onClose={() => setExcelExportInfo(null)}
        t={t}
      />
    </section>
  );
};
